import React from "react";
import { useSeason } from "../context/SeasonContext";
import { CheckCircle, Trophy, Star, Clock, Lock } from "lucide-react";

// Status metadata for visual styling
const STATUS_META = {
  upcoming: {
    label: "Upcoming",
    icon: Clock,
    color: "text-gray-500 dark:text-gray-400",
    bgColor: "bg-gray-100 dark:bg-gray-800",
    borderColor: "border-gray-300 dark:border-gray-700",
    activeBg: "bg-gray-200 dark:bg-gray-700",
  },
  registration: {
    label: "Registration",
    icon: Star,
    color: "text-blue-600 dark:text-blue-400",
    bgColor: "bg-blue-50 dark:bg-blue-900/20",
    borderColor: "border-blue-300 dark:border-blue-700",
    activeBg: "bg-blue-100 dark:bg-blue-900/40",
  },
  active: {
    label: "Regular Season",
    icon: CheckCircle,
    color: "text-green-600 dark:text-green-400",
    bgColor: "bg-green-50 dark:bg-green-900/20",
    borderColor: "border-green-300 dark:border-green-700",
    activeBg: "bg-green-100 dark:bg-green-900/40",
  },
  playoffs: {
    label: "Playoffs",
    icon: Trophy,
    color: "text-orange-600 dark:text-orange-400",
    bgColor: "bg-orange-50 dark:bg-orange-900/20",
    borderColor: "border-orange-300 dark:border-orange-700",
    activeBg: "bg-orange-100 dark:bg-orange-900/40",
  },
  completed: {
    label: "Completed",
    icon: Lock,
    color: "text-gray-500 dark:text-gray-400",
    bgColor: "bg-gray-100 dark:bg-gray-800",
    borderColor: "border-gray-300 dark:border-gray-700",
    activeBg: "bg-gray-200 dark:bg-gray-700",
  },
};

const StandingsSeasonSelector = () => {
  const { seasons, selectedSeason, setSelectedSeason, activeSeason } =
    useSeason();

  if (!seasons || seasons.length === 0) return null;

  // Show only seasons that have started or are in registration (not just "upcoming")
  const visibleSeasons = seasons.filter(
    (s) => s.status !== "upcoming" || s.id === activeSeason?.id,
  );

  if (visibleSeasons.length <= 1) return null;

  return (
    <div className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/30 px-4 py-3">
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">
          Season:
        </span>
        <div className="flex gap-2">
          {visibleSeasons.map((season) => {
            const isSelected = selectedSeason?.id === season.id;
            const isActive = season.id === activeSeason?.id;
            const meta = STATUS_META[season.status] || STATUS_META.upcoming;
            const Icon = meta.icon;

            return (
              <button
                key={season.id}
                onClick={() => setSelectedSeason(season)}
                className={`
                  flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold
                  border transition-all duration-200 shrink-0
                  ${
                    isSelected
                      ? `${meta.activeBg} ${meta.borderColor} ${meta.color} shadow-sm`
                      : `${meta.bgColor} ${meta.borderColor} ${meta.color} hover:${meta.activeBg}`
                  }
                `}
              >
                <Icon size={14} className={isSelected ? "animate-pulse" : ""} />
                <span className="whitespace-nowrap">{season.name}</span>
                {isActive && (
                  <span className="inline-flex items-center justify-center w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StandingsSeasonSelector;
